import { Text } from '@village/ui';
import { InputForm } from 'components/fields/input-form';
import { MaskedInputForm } from 'components/fields/masked-input-form';
import { RadioForm } from 'components/fields/radio-form';
import { useCountly } from 'epion-checkin/hooks/use-countly';
import { EpionPatientInformationFormSchema } from 'epion-checkin/schema';
import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import * as Styled from './styles';

export const PatientInformationFormInputs: FC = () => {
    const { t } = useTranslation();
    const { addCountlyEvent } = useCountly();
    const { setValue, watch } = useFormContext<EpionPatientInformationFormSchema>();
    const [state, noEmail] = watch(['state', 'noEmail']);

    useEffect(() => {
        if (state) {
            setValue('state', state.toUpperCase() as EpionPatientInformationFormSchema['state']);
        }
    }, [setValue, state]);

    useEffect(() => {
        if (noEmail) {
            setValue('email', undefined, { shouldValidate: true });
            addCountlyEvent({ key: 'viewpatientintake.clicknoemail' });
        }
    }, [setValue, noEmail, addCountlyEvent]);

    return (
        <Styled.FormContainer>
            <Styled.FieldsGroupsContainer>
                <Styled.FieldsGroup>
                    <Text type="h5" fontWeight={700}>
                        {t('form.patientInfo.title')}
                    </Text>
                    <Styled.ResponsiveFields $template="1fr 1fr">
                        <InputForm label={t('form.fields.firstName.label')} name="firstName" fullWidth={true} />
                        <InputForm label={t('form.fields.lastName.label')} name="lastName" fullWidth={true} />
                    </Styled.ResponsiveFields>
                    <Styled.ResponsiveFields $template="1fr 1fr">
                        <MaskedInputForm
                            name="dateOfBirth"
                            inputMode="numeric"
                            mask="00/00/0000"
                            label={t('form.fields.dateOfBirth.label')}
                            placeholder="mm/dd/yyyy"
                            fullWidth={true}
                            eager={true}
                        />
                        <RadioForm
                            id="select-sex"
                            label={t('form.fields.sex.label')}
                            name="sex"
                            options={[
                                { value: 'FEMALE', label: t('form.fields.sex.female') },
                                { value: 'MALE', label: t('form.fields.sex.male') },
                            ]}
                        />
                    </Styled.ResponsiveFields>
                </Styled.FieldsGroup>

                <Styled.FieldsGroup>
                    <Text type="h5" fontWeight={700}>
                        {t('form.address.title')}
                    </Text>
                    <Styled.ResponsiveFields $template="1fr 1fr">
                        <InputForm label={t('form.fields.address1.label')} name="address" fullWidth={true} />
                        <InputForm label={t('form.fields.address2.label')} name="addressCount" fullWidth={true} />
                    </Styled.ResponsiveFields>
                    <Styled.ResponsiveFields $template="1fr 1fr 1fr">
                        <InputForm label={t('form.fields.city.label')} name="city" fullWidth={true} />
                        <InputForm
                            label={t('form.fields.state.label')}
                            name="state"
                            fullWidth={true}
                            maxLength={2}
                            autoCapitalize="capitalize" // Brings up the all-caps mobile keyboard
                        />
                        <InputForm
                            label={t('form.fields.zip.label')}
                            name="zip"
                            fullWidth={true}
                            type="number"
                            inputMode="numeric"
                        />
                    </Styled.ResponsiveFields>
                </Styled.FieldsGroup>

                <Styled.FieldsGroup>
                    <Text type="h5" fontWeight={700}>
                        {t('form.contact.title')}
                    </Text>
                    <Styled.ResponsiveFields $template="1fr 1fr 1fr">
                        <Styled.EmailFields>
                            <InputForm
                                label={t('form.fields.email.label')}
                                name="email"
                                fullWidth={true}
                                aria-label="Email"
                                type="email"
                                disabled={!!noEmail}
                            />
                            <Styled.NoEmailContainer>
                                <Styled.Checkbox
                                    label={t('form.fields.noEmail.label')}
                                    name="noEmail"
                                    onChange={(_event, checked) => setValue('noEmail', checked)}
                                    checked={noEmail}
                                />
                            </Styled.NoEmailContainer>
                        </Styled.EmailFields>
                        <MaskedInputForm
                            label={t('form.fields.phone.label')}
                            name="phoneNumber"
                            type="tel"
                            inputMode="tel"
                            mask="(000) 000-0000"
                            placeholder="(___) ___-____"
                            fullWidth={true}
                            eager={true}
                        />
                        <RadioForm
                            label={t('form.fields.phoneType.label')}
                            id="select-phone-type"
                            name="phoneType"
                            options={[
                                { value: 'MOBILE', label: t('form.fields.phoneType.mobile') },
                                { value: 'HOME', label: t('form.fields.phoneType.home') },
                            ]}
                        />
                    </Styled.ResponsiveFields>
                </Styled.FieldsGroup>
            </Styled.FieldsGroupsContainer>
        </Styled.FormContainer>
    );
};
